import React from 'react';
import AnimatedSection from '../../common/AnimatedSection';
import { BuildingOfficeIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import partnersData from '../../../data/partenairesData.json';

export default function PartnersHomeSection() {
  const associations = partnersData.associations || [];
  const etablissements = partnersData.etablissements || [];

  return (
    <AnimatedSection
      animation="fade-up"
      className="relative overflow-hidden w-full max-[984px]:rounded-none min-[985px]:rounded-[24px] max-w-7xl mx-auto my-16 p-6 sm:p-10 border border-white/20 shadow-acem-xl"
    >
      {/* Image de fond des Comores avec voile sombre et dégradé */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden max-[984px]:rounded-none min-[985px]:rounded-[24px]">
        <img
          src="/images/comoros_bg.jpg"
          alt="Paysage des Comores"
          className="absolute inset-0 w-full h-full object-cover object-center scale-105 filter brightness-90"
        />
        <div
          className="absolute inset-0 bg-gradient-to-br from-black/90 via-black/75 to-black/90 backdrop-blur-[2px]"
          aria-hidden="true"
        />
      </div>

      {/* Contenu principal */}
      <div className="relative z-10 space-y-10">
        {/* En-tête de la section */}
        <div className="text-center space-y-3 max-w-2xl mx-auto">
          <span className="px-4 py-1.5 rounded-full text-xs font-extrabold bg-acem-accent/20 text-acem-accent border border-acem-accent/40 backdrop-blur-md uppercase tracking-wider">
            Réseau & Coopérations
          </span>
          <h2 className="font-title text-2xl sm:text-3xl md:text-4xl font-extrabold text-white leading-tight drop-shadow-md">
            Nos Partenaires
          </h2>
          <p className="text-sm sm:text-base text-white/85 max-w-xl mx-auto leading-relaxed">
            Les associations, établissements académiques et centres partenaires qui accompagnent l'ACEM Fès au quotidien.
          </p>
        </div>

        {/* Sous-section: Associations Partenaires */}
        <div className="space-y-4">
          <div className="flex items-center gap-2.5 text-white border-b border-white/15 pb-3">
            <UserGroupIcon className="w-6 h-6 text-acem-accent" />
            <h3 className="font-title text-lg sm:text-xl font-bold tracking-wide text-white">
              Associations Partenaires
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {associations.map((asso, idx) => (
              <div
                key={idx}
                className="partner-card bg-white/10 hover:bg-white/20 border border-white/20 hover:border-acem-accent/60 backdrop-blur-md rounded-[16px] p-5 transition-all duration-300 hover:-translate-y-1 shadow-md flex items-center justify-between gap-4 group"
              >
                <div className="space-y-1">
                  <span className="text-[11px] font-bold text-acem-accent/90 uppercase tracking-wider block">
                    {asso.type}
                  </span>
                  <h4 className="font-title text-base sm:text-lg font-bold text-white group-hover:text-amber-300 transition-colors leading-snug">
                    {asso.nom}
                  </h4>
                  {asso.nomAr && (
                    <p className="text-xs font-semibold text-white/70 font-sans">
                      {asso.nomAr}
                    </p>
                  )}
                </div>
                <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center text-white/80 group-hover:bg-acem-primary group-hover:text-white transition-all flex-shrink-0 shadow-sm">
                  <UserGroupIcon className="w-5 h-5" />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Sous-section: Établissements & Centres Partenaires */}
        <div className="space-y-4 pt-2">
          <div className="flex items-center gap-2.5 text-white border-b border-white/15 pb-3">
            <BuildingOfficeIcon className="w-6 h-6 text-acem-accent" />
            <h3 className="font-title text-lg sm:text-xl font-bold tracking-wide text-white">
              Établissements & Centres Partenaires
            </h3>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {etablissements.map((etab, idx) => (
              <div
                key={idx}
                className="partner-card bg-white/10 hover:bg-white/20 border border-white/20 hover:border-acem-accent/60 backdrop-blur-md rounded-[16px] p-5 transition-all duration-300 hover:-translate-y-1 shadow-md flex items-center justify-between gap-4 group"
              >
                <div className="space-y-1">
                  <span className="text-[11px] font-bold text-acem-accent/90 uppercase tracking-wider block">
                    {etab.type}
                  </span>
                  <h4 className="font-title text-base font-bold text-white group-hover:text-amber-300 transition-colors leading-snug">
                    {etab.nom}
                  </h4>
                  {etab.nomAr && (
                    <p className="text-xs font-semibold text-white/70 font-sans">
                      {etab.nomAr}
                    </p>
                  )}
                </div>
                <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center text-white/80 group-hover:bg-acem-primary group-hover:text-white transition-all flex-shrink-0 shadow-sm">
                  <BuildingOfficeIcon className="w-5 h-5" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </AnimatedSection>
  );
}
